import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import OptimizedImage from '../components/OptimizedImage';

const Menu = () => {
  const [menuData, setMenuData] = useState(null);
  const [visibleCategories, setVisibleCategories] = useState([]);
  const [sectionTitles, setSectionTitles] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMenuData();
    fetchVisibleCategories();
    fetchSectionTitles();
  }, []);

  const fetchMenuData = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/menu-items`);
      if (response.ok) {
        const data = await response.json();
        setMenuData(data.filter(item => item.active));
      }
    } catch (error) {
      console.error('Error fetching menu:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVisibleCategories = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/menu-items/categories/visible`);
      if (response.ok) {
        const data = await response.json();
        setVisibleCategories(data.map(c => c.id));
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Default to all visible if fetch fails
      setVisibleCategories(['specialty', 'stackers', 'scoops', 'treats', 'pastries', 'drinks', 'energy_flavors', 'ice_cream_flavors']);
    }
  };

  const fetchSectionTitles = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/menu-sections/titles`);
      if (response.ok) {
        const data = await response.json();
        setSectionTitles(data);
      }
    } catch (error) {
      console.error('Error fetching section titles:', error);
      // Use defaults if fetch fails
      setSectionTitles({
        ice_cream_nachos: "Ice Cream Nachos",
        ice_cream_stackers: "Ice Cream Stackers",
        scoops: "Scoops",
        milkshakes: "Milk Shakes",
        ice_cream_treats: "Ice Cream Treats",
        energy_drinks: "Energy Drinks",
        coffees: "Coffees",
        pastries: "Pastries",
        all_specialties: "All Our Specialties",
        seasonal_flavors: "Seasonal Flavors"
      });
    }
  };

  // Helper to get section title
  const getSectionTitle = (key, fallback) => sectionTitles[key] || fallback;

  // Helper to check if a category is visible
  const isCategoryVisible = (category) => {
    return visibleCategories.length === 0 || visibleCategories.includes(category);
  };

  // Helper function to get items by category (only items displayed on Menu page)
  const getItemsByCategory = (category) => {
    if (!menuData) return [];
    return menuData
      .filter(item => item.category === category)
      .filter(item => !item.display_page || item.display_page === 'menu' || item.display_page === 'both')
      .sort((a, b) => a.order - b.order);
  };

  // Get a single item by category (for sections with one main item)
  const getMainItemByCategory = (category) => {
    const items = getItemsByCategory(category);
    return items.length > 0 ? items[0] : null;
  };

  // Category Badge Component
  const CategoryBadge = ({ children }) => (
    <div className="inline-block mb-8">
      <div 
        className="px-8 py-4 text-white font-bold text-xl rounded-full"
        style={{ 
          background: 'linear-gradient(135deg, #E91E8C 0%, #9B4DCA 50%, #4A9A8D 100%)',
          fontFamily: 'Rowdies, cursive'
        }}
      >
        {children}
      </div>
    </div>
  );

  // Dotted line separator
  const DottedLine = () => (
    <div className="border-b-2 border-dotted border-gray-300 my-12"></div>
  );

  // Price display component for sizes
  const SizePrices = ({ sizes, layout = 'vertical' }) => {
    if (!sizes || sizes.length === 0) return null;
    
    if (layout === 'horizontal') {
      return (
        <div className="flex flex-wrap gap-10 lg:gap-20">
          {sizes.map((size, index) => (
            <div key={index}>
              <span className="font-bold text-lg" style={{ fontFamily: 'Rowdies, cursive' }}>
                {size.name}
              </span>
              <span className="text-pink-600 font-bold text-xl ml-3">${size.price?.toFixed(2)}</span>
            </div>
          ))}
        </div>
      );
    }
    
    return (
      <div className="text-left min-w-[150px]">
        {sizes.map((size, index) => (
          <p key={index} className={index < sizes.length - 1 ? "mb-3" : ""}>
            <span className="font-bold text-xl" style={{ fontFamily: 'Rowdies, cursive' }}>
              {size.name}
            </span>
            <span className="text-pink-600 font-bold text-2xl ml-4">${size.price?.toFixed(2)}</span>
          </p>
        ))}
      </div>
    );
  };

  // Ice Cream Nachos from CMS (category 'ice_cream_nachos'), with hardcoded fallback
  const nachosItems = getItemsByCategory('ice_cream_nachos');
  const largeNacho = nachosItems.find(i => /large/i.test(i.name || '')) || {
    name: 'Large Ice Cream Nachos',
    price: 14.00,
    image_url: '/images/specialties/nachos.png',
    description: 'Our special waffle chips, your choice two ice creams, two sauces, three toppings'
  };
  const personalNacho = nachosItems.find(i => /personal/i.test(i.name || '')) || {
    name: 'Personal Ice Cream Nachos',
    price: 8.00,
    image_url: '/images/products/personal-nachos.png',
    description: 'Our special waffle chips, your choice one ice cream, one topping, one sauce'
  };
  const nachosSizes = [
    { name: 'Personal', price: personalNacho.price },
    { name: 'Large', price: largeNacho.price }
  ];
  
  const stackers = getItemsByCategory('stackers');
  const stackerPrices = stackers.length > 0 && stackers[0].sizes ? stackers[0].sizes : [
    { name: 'Regular', price: 7.00 },
    { name: 'Large', price: 9.00 }
  ];

  const scoops = getMainItemByCategory('scoops') || {
    sizes: [{ name: 'Baby', price: 3.50 }, { name: 'Single', price: 4.50 }, { name: 'Double', price: 5.50 }]
  };

  const milkshakes = getMainItemByCategory('drinks');
  const milkshakePrices = milkshakes?.sizes || [{ name: 'Regular', price: 7.00 }, { name: 'Large', price: 9.00 }];

  const treats = getItemsByCategory('treats');
  
  const energyDrinks = menuData?.find(item => item.name === 'Energy Drinks') || {
    description: 'This all-natural iced energy drink combines the power of Coffeeberry Energy®, green coffee extract, L-theanine, vitamin B12, and guarana. Authentic fruit flavors and colors. Delicious alone, or blend with one or more of our natural fruit flavors.',
    sizes: [{ name: 'Regular', price: 4.95, description: '120 mg caffeine' }, { name: 'Large', price: 5.95, description: '160 mg caffeine' }],
    image_url: '/images/products/energy-drinks.png'
  };

  const energyFlavors = getItemsByCategory('energy_flavors');

  const coffee = menuData?.find(item => item.name === 'Coffee') || {
    sizes: [
      { name: 'Brewed', price: 3.50 },
      { name: 'Latte', price: 4.50 },
      { name: 'Espresso', price: 3.50 },
      { name: 'Cappuccino', price: 4.00 }
    ],
    image_url: '/images/products/coffee.png'
  };

  const iceCreamFlavors = getItemsByCategory('ice_cream_flavors');

  // Pastries from CMS (handle both 'pastries' and 'pastry' category names)
  const pastries = getItemsByCategory('pastries').length > 0 
    ? getItemsByCategory('pastries') 
    : getItemsByCategory('pastry');

  // Popular Flavors - from CMS or defaults
  const popularFlavors = iceCreamFlavors.length > 0 
    ? iceCreamFlavors.slice(0, 14).map(f => ({ name: f.name, image: f.image_url }))
    : [
        { name: 'Butter Pecan', image: '/images/icecream/butterpecan.webp' },
        { name: 'Chocolate Chip Cookie Dough', image: '/images/icecream/chocolatechipcookiedough.webp' },
        { name: 'Coffee Almond Fudge', image: '/images/icecream/coffeealmondfudge.webp' },
        { name: 'Cookies And Cream', image: '/images/icecream/cookiesandcream.webp' },
        { name: 'Mint Chocolate Chip', image: '/images/icecream/mintchocolatechip.webp' },
        { name: 'Mud Slide', image: '/images/icecream/Mudslide.webp' },
        { name: 'Oregon Strawberry', image: '/images/icecream/oregonstrawberry.webp' },
        { name: 'Peaches And Cream', image: '/images/icecream/peachesandcream__1_.webp' },
        { name: 'Rocky Road', image: '/images/icecream/rockyroad.webp' },
        { name: 'Vanilla', image: '/images/icecream/vanilla.webp' },
        { name: 'White Chocolate Raspberry', image: '/images/icecream/whitechocolateraspberry.webp' },
        { name: 'Blackberry Pie', image: '/images/icecream/blackberrypie.webp' },
        { name: 'Chocolate Peanut Butter', image: '/images/icecream/chocolatepeanutbutter.webp' },
        { name: 'Oregon Dark Cherry', image: '/images/icecream/oregondarkcherry.webp' }
      ];

  // Seasonal Flavors (hardcoded as they are a curated list)
  const seasonalFlavorsCol1 = [
    { name: 'Mudslide', starred: true },
    { name: 'Oregon Dark Cherry', starred: true },
    { name: 'Rocky Road', starred: true },
    { name: 'Mountain Huckleberry', starred: true },
    { name: 'Blackberry Pie', starred: true },
    { name: 'Chocolate Peanut Butter', starred: true },
    { name: 'Hazelnut Salted Caramel', starred: true },
    { name: 'Chocolate Chip Cookie Dough', starred: true },
    { name: 'White Chocolate Raspberry Yum', starred: true },
  ];

  const seasonalFlavorsCol2 = [
    { name: 'Caramel Toffee Crunch', starred: true },
    { name: 'Classic Vanilla', starred: false },
    { name: 'Coffee Almond Fudge', starred: true },
    { name: 'Mint Chocolate Chip', starred: true },
    { name: 'Strawberry Oregon', starred: true },
    { name: 'Cookies and Cream', starred: true },
    { name: 'Peanut Butter Cup', starred: true },
    { name: 'Peaches N Cream', starred: true },
    { name: 'Butter Pecan', starred: true },
    { name: 'Dutch Chocolate', starred: false },
  ];

  const seasonalFlavorsCol3 = [
    { name: 'Blue Monster', starred: false },
    { name: 'Krazy Kookie Dough', starred: false },
    { name: 'Coconut', starred: false },
    { name: 'Banana Pudding', starred: false },
    { name: 'Birthday Cake', starred: false },
    { name: 'Gooey Butter Cake', starred: false },
    { name: 'Salted Caramel Brownie', starred: false },
    { name: 'Peppermint', starred: false },
    { name: 'Strawberry Cheesecake', starred: false },
    { name: 'Milk Chocolate', starred: false },
  ];

  // Default stacker items if none in CMS
  const defaultStackers = [
    { name: 'Death By Chocolate', description: 'Mudslide Ice cream, oreos, brownies, chocolate chips, hot fudge', image_url: '/images/products/death-by-chocolate.png' },
    { name: 'Biscoff Supreme', description: 'Vanilla ice cream, Biscoff crumbles, cookie butter sauce and cheesecake bites', image_url: '/images/products/biscoff-supreme.png' },
    { name: 'PB Overload', description: 'Chocolate PB ice cream, reeses, pb chips, pretzels, PB sauce, nutella', image_url: '/images/products/pb-overload.png' },
    { name: 'Strawberry Shortcake', description: 'Strawberry ice cream, vanilla wafers, cheesecake bites, strawberry sauce', image_url: '/images/products/strawberry-shortcake-stacker.png' },
    { name: 'Salted Cookie', description: 'Hazelnut Salted Caramel, cookie dough, pretzels, pecans, caramel and hot fudge', image_url: '/images/products/salted-cookie.png' },
    { name: 'Create Your Own Stacker Large', description: 'Two ice cream scoops, two toppings, one sauce', image_url: '/images/products/stacker-outline.svg' },
    { name: 'Create Your Own Stacker Regular', description: 'One ice cream scoop, two toppings, one sauce', image_url: '/images/products/stacker-outline.svg' },
  ];

  const displayStackers = stackers.length > 0 ? stackers : defaultStackers;

  // Default treats if none in CMS
  const defaultTreats = [
    { name: 'Classic Banana Split', price: 12.00, description: 'Banana, 3 huge scoops of chocolate, strawberry, and vanilla ice cream, strawberry sauce, pineapple sauce, hot fudge, whip cream, nuts, cherries', image_url: '/images/products/banana-split.png' },
    { name: 'Brownie Boat', price: 12.00, description: 'Three warm brownie wedges nestled between your choice of 2 huge scoops of ice cream and a sauce, topped with whipped cream and cherries', image_url: '/images/products/brownie-boat.png' },
    { name: 'Chocolate Chip Lava Cookie Sundae', price: 12.00, description: 'Warm cookie with your choice of ice cream and sauce. Topped with whipped cream and a cherry', image_url: '/images/products/lava-cookie-sundae.png' },
    { name: 'Ice Cream Flight', price: 9.00, description: '4 sample serving sizes of ice cream made to go', image_url: '/images/products/ice-cream-flight.png' },
    { name: 'Pup Cup (FOR DOGS)', price: 3.00, description: 'Whipped cream paired with a dog treat', image_url: '/images/products/pup-cup.png' },
  ];

  const displayTreats = treats.length > 0 ? treats : defaultTreats;

  // Default energy flavors
  const defaultEnergyFlavors = [
    { name: 'Yumberry Red', description: 'Exotic berries' },
    { name: 'Dragonfruit Pink', description: 'Authentic dragonfruit, slightly sweet reminiscent of kiwi, pear and watermelon in one' },
    { name: 'Starfruit Yellow', description: 'Tart, tropical' },
    { name: 'Glacier Clear', description: 'Light, refreshing taste' },
    { name: 'Powerfruit Purple', description: 'Acai superfruit, slightly tart with a flavor like dark chocolate combined with berries' },
    { name: 'Sugar-free flavors', description: 'Berry Blue, Glacier Clear, Dragonfruit Pink' },
    { name: 'Berry Blue', description: 'Raspberry and Honeyberry a tangy, sweet cross between blueberry and raspberry' },
  ];

  const displayEnergyFlavors = energyFlavors.length > 0 ? energyFlavors : defaultEnergyFlavors;

  return (
    <div className="min-h-screen" style={{ fontFamily: 'Outfit, sans-serif' }}>
      <SEO 
        title="Menu"
        description="View our full menu of all-natural ice cream made with the highest quality ingredients - ice cream stackers, sundaes, milkshakes, energy drinks, coffee, and fresh pastries at 1st Ave ICP in Mineral Wells, TX."
        keywords="ice cream menu Mineral Wells, dessert menu Texas, ice cream stackers, milkshakes, sundaes, pastries Mineral Wells TX"
        canonical="/menu"
      />
      {/* Hero Section */}
      <section 
        className="pt-28 lg:pt-32 pb-16 px-4"
        style={{
          backgroundImage: 'url(/images/misc/storefront-bg.jpg)',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat', backgroundPosition: 'center', backgroundAttachment: 'fixed'
        }}
      >
        <div className="max-w-7xl mx-auto text-center">
          <h1 
            className="text-6xl md:text-7xl lg:text-8xl font-bold text-black mb-4"
            style={{ fontFamily: 'Rowdies, cursive' }}
          >
            Menu
          </h1>
          <div className="flex items-center justify-center gap-2 text-base">
            <Link to="/" className="text-pink-600 hover:underline">🏠 Home</Link>
            <span className="text-pink-600">»</span>
            <span className="text-pink-600">Menu</span>
          </div>
        </div>
      </section>

      {/* Main Menu Content */}
      <section 
        className="py-8 px-4 lg:px-8"
        style={{
          backgroundImage: 'url(/images/misc/storefront-bg.jpg)',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat', backgroundPosition: 'center', backgroundAttachment: 'fixed'
        }}
      >
        <div className="max-w-6xl mx-auto">
          
          {/* Ice Cream Nachos Section */}
          <div className="mb-4">
            <CategoryBadge>{getSectionTitle('ice_cream_nachos', 'Ice Cream Nachos')}</CategoryBadge>
            
            <div className="flex flex-wrap items-start gap-12 lg:gap-20">
              {/* Prices */}
              <SizePrices sizes={nachosSizes} layout="vertical" />

              {/* Large Ice Cream Nachos */}
              <div className="text-center max-w-[280px]">
                <OptimizedImage 
                  src={largeNacho.image_url || '/images/specialties/nachos.png'}
                  alt={largeNacho.name || 'Large Ice Cream Nachos'} 
                  className="w-48 h-36 object-contain mx-auto mb-4"
                  width={192}
                  height={144}
                />
                <h3 className="font-bold text-xl mb-2" style={{ fontFamily: 'Rowdies, cursive' }}>{largeNacho.name || 'Large Ice Cream Nachos'}</h3>
                <p className="text-sm text-gray-700">{largeNacho.description}</p>
              </div>

              {/* Personal Ice Cream Nachos */}
              <div className="text-center max-w-[280px]">
                <OptimizedImage 
                  src={personalNacho.image_url || '/images/products/personal-nachos.png'} 
                  alt={personalNacho.name || 'Personal Ice Cream Nachos'} 
                  className="w-48 h-36 object-contain mx-auto mb-4"
                  width={192}
                  height={144}
                />
                <h3 className="font-bold text-xl mb-2" style={{ fontFamily: 'Rowdies, cursive' }}>{personalNacho.name || 'Personal Ice Cream Nachos'}</h3>
                <p className="text-sm text-gray-700">{personalNacho.description}</p>
              </div>
            </div>
          </div>

          <DottedLine />

          {/* Ice Cream Stackers Section */}
          <div className="mb-4">
            <CategoryBadge>{getSectionTitle('ice_cream_stackers', 'Ice Cream Stackers')}</CategoryBadge>
            
            {/* Prices Row */}
            <div className="mb-10">
              {stackerPrices.map((size, index) => (
                <p key={index} className={index < stackerPrices.length - 1 ? "mb-3" : ""}>
                  <span className="font-bold text-xl" style={{ fontFamily: 'Rowdies, cursive' }}>{size.name}</span>
                  <span className="text-pink-600 font-bold text-2xl ml-4">${size.price?.toFixed(2)}</span>
                </p>
              ))}
            </div>

            {/* Stackers Grid - 3 columns */}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-10">
              {displayStackers.map((stacker) => (
                <div key={stacker.id || stacker.name} className="text-center">
                  <OptimizedImage 
                    src={stacker.image_url || '/images/products/stacker-outline.svg'} 
                    alt={stacker.image_alt || `${stacker.name} Ice Cream Stacker at 1st Ave ICP`} 
                    className="w-36 h-44 object-contain mx-auto mb-3"
                    width={144}
                    height={176}
                  />
                  <h3 className="font-bold text-lg mb-2" style={{ fontFamily: 'Rowdies, cursive' }}>{stacker.name}</h3>
                  <p className="text-sm text-gray-700">{stacker.description}</p>
                </div>
              ))}
            </div>
          </div>

          <DottedLine />

          {/* Scoops Section */}
          <div className="mb-4">
            <CategoryBadge>{getSectionTitle('scoops', 'Scoops')}</CategoryBadge>
            
            <div className="flex flex-wrap gap-8 lg:gap-16">
              {(scoops.sizes || []).map((size, index) => (
                <div key={index} className="text-center">
                  {size.image_url && (
                    <OptimizedImage 
                      src={size.image_url} 
                      alt={size.image_alt || `${size.name} Ice Cream Scoop at 1st Ave ICP`}
                      className="w-24 h-24 lg:w-32 lg:h-32 object-contain mx-auto mb-3"
                      width={128}
                      height={128}
                      priority={true}
                      lazy={false}
                    />
                  )}
                  <p>
                    <span className="font-bold text-lg lg:text-xl" style={{ fontFamily: 'Rowdies, cursive' }}>{size.name}</span>
                    <span className="text-pink-600 font-bold text-xl lg:text-2xl ml-3">${size.price?.toFixed(2)}</span>
                  </p>
                </div>
              ))}
            </div>
          </div>

          <DottedLine />

          {/* Milk Shakes Section */}
          <div className="mb-4">
            <CategoryBadge>{getSectionTitle('milkshakes', 'Milk Shakes')}</CategoryBadge>
            
            <div className="flex flex-wrap items-end gap-12">
              {milkshakePrices.map((size, index) => (
                <div key={index} className="text-center">
                  <OptimizedImage 
                    src="/images/products/milkshake.png" 
                    alt={`${size.name} Milkshake`}
                    className="w-28 h-44 object-contain mx-auto mb-3"
                    width={112}
                    height={176}
                  />
                  <p>
                    <span className="font-bold text-lg" style={{ fontFamily: 'Rowdies, cursive' }}>{size.name}</span>
                    <span className="text-pink-600 font-bold text-xl ml-3">${size.price?.toFixed(2)}</span>
                  </p>
                </div>
              ))}
            </div>
          </div>

          <DottedLine />

          {/* Ice Cream Treats Section */}
          <div className="mb-4">
            <CategoryBadge>{getSectionTitle('ice_cream_treats', 'Ice Cream Treats')}</CategoryBadge>
            
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-10">
              {displayTreats.map((treat) => (
                <div key={treat.id || treat.name}>
                  <OptimizedImage 
                    src={treat.image_url || '/images/products/stacker-outline.svg'} 
                    alt={treat.image_alt || `${treat.name} Ice Cream Treat at 1st Ave ICP`} 
                    className="w-48 h-32 object-contain mb-3"
                    width={192}
                    height={128}
                  />
                  <div className="flex items-baseline gap-3 mb-2 flex-wrap">
                    <h3 className="font-bold text-lg" style={{ fontFamily: 'Rowdies, cursive' }}>{treat.name}</h3>
                    <span className="text-pink-600 font-bold text-xl">${treat.price?.toFixed(2)}</span>
                  </div>
                  <p className="text-sm text-gray-700">{treat.description}</p>
                </div>
              ))}
            </div>
          </div>

          <DottedLine />

          {/* Energy Drinks Section */}
          <div className="mb-4">
            <CategoryBadge>{getSectionTitle('energy_drinks', 'Energy Drinks')}</CategoryBadge>
            
            <div className="grid lg:grid-cols-2 gap-8 mb-8">
              {/* Left - Image and Pricing */}
              <div>
                <OptimizedImage 
                  src={energyDrinks.image_url || "/images/products/energy-drinks.png"}
                  alt="Energy Drinks" 
                  className="w-72 h-52 object-contain mb-6"
                  width={288}
                  height={208}
                />
                {(energyDrinks.sizes || []).map((size, index) => (
                  <p key={index} className={index < energyDrinks.sizes.length - 1 ? "mb-3" : ""}>
                    <span className="font-bold text-lg" style={{ fontFamily: 'Rowdies, cursive' }}>
                      {size.name} {size.description}
                    </span>
                    <span className="text-pink-600 font-bold text-xl ml-4">${size.price?.toFixed(2)}</span>
                  </p>
                ))}
              </div>
              
              {/* Right - Description */}
              <div className="flex items-center">
                <p className="text-gray-700">
                  {energyDrinks.description}
                </p>
              </div>
            </div>
            
            {/* Flavors Grid */}
            <div className="grid md:grid-cols-2 gap-x-12 gap-y-4">
              {displayEnergyFlavors.map((flavor, index) => (
                <div key={index}>
                  <span className="font-bold" style={{ fontFamily: 'Rowdies, cursive' }}>{flavor.name}</span>
                  <span className="text-gray-600 text-sm ml-3">{flavor.description}</span>
                </div>
              ))}
            </div>
          </div>

          <DottedLine />

          {/* Coffees Section */}
          <div className="mb-4">
            <CategoryBadge>{getSectionTitle('coffees', 'Coffees')}</CategoryBadge>
            
            <div className="flex flex-wrap items-end gap-8">
              <OptimizedImage 
                src={coffee.image_url || "/images/products/coffee.png"}
                alt="Coffee" 
                className="w-28 h-40 object-contain"
                width={112}
                height={160}
              />
              <div className="flex flex-wrap gap-10 lg:gap-20">
                {(coffee.sizes || []).map((size, index) => (
                  <div key={index}>
                    <span className="font-bold text-lg" style={{ fontFamily: 'Rowdies, cursive' }}>{size.name}</span>
                    <span className="text-pink-600 font-bold text-xl ml-3">${size.price?.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Pastries Section - Conditionally Visible */}
          {isCategoryVisible('pastries') && pastries.length > 0 && (
            <>
              <DottedLine />
              <div className="mb-4">
                <CategoryBadge>{getSectionTitle('pastries', 'Pastries')}</CategoryBadge>
                
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {pastries.map((item, index) => (
                    <div key={index} className="text-center">
                      <div className="w-32 h-32 lg:w-40 lg:h-40 rounded-lg overflow-hidden mb-3 shadow-md mx-auto bg-white">
                        <OptimizedImage
                          src={item.image_url || `/images/pastries/pastry-${index + 1}.png`}
                          alt={item.image_alt || `${item.name} Fresh Pastry at 1st Ave ICP`}
                          className="w-full h-full object-cover"
                          width={160}
                          height={160}
                        />
                      </div>
                      <h3 
                        className="text-base lg:text-lg font-bold text-black"
                        style={{ fontFamily: 'Rowdies, cursive' }}
                      >
                        {item.name}
                      </h3>
                      {item.show_price && item.price && (
                        <p className="text-pink-600 font-bold mt-1">${item.price.toFixed(2)}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Popular Flavors Card Section */}
      <section 
        className="py-12 px-4"
        style={{
          backgroundImage: 'url(/images/misc/storefront-bg.jpg)',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat', backgroundPosition: 'center', backgroundAttachment: 'fixed'
        }}
      >
        <div className="max-w-5xl mx-auto">
          <div className="bg-white rounded-lg p-8 lg:p-12 border-[6px] border-[#E91E8C] shadow-2xl relative">
            {/* Logo at top */}
            <div className="flex justify-center -mt-16 lg:-mt-20 mb-8">
              <OptimizedImage
                src="/images/logo/logo-circle.png"
                alt="1st Ave ICP Logo"
                className="w-24 h-24 lg:w-32 lg:h-32 rounded-full shadow-lg bg-white"
                width={128}
                height={128}
                priority={true}
              />
            </div>
            
            <h2 
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-black text-center mb-12"
              style={{ fontFamily: 'Rowdies, cursive' }}
            >
              Popular Flavors
            </h2>
            
            {/* Ice Cream Grid - 4 columns */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 lg:gap-8">
              {popularFlavors.map((flavor) => (
                <div key={flavor.name} className="text-center">
                  <div className="w-32 h-32 lg:w-40 lg:h-40 rounded-full overflow-hidden mb-4 shadow-md mx-auto">
                    <OptimizedImage
                      src={flavor.image}
                      alt={flavor.name}
                      className="w-full h-full object-cover"
                      width={160}
                      height={160}
                    />
                  </div>
                  <h3 
                    className="text-base lg:text-lg font-bold text-black"
                    style={{ fontFamily: 'Rowdies, cursive' }}
                  >
                    {flavor.name}
                  </h3>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Flavors That Rotate Seasonally Section */}
      <section 
        className="py-16 px-4"
        style={{
          backgroundImage: 'url(/images/misc/storefront-bg.jpg)',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat', backgroundPosition: 'center', backgroundAttachment: 'fixed'
        }}
      >
        <div className="max-w-5xl mx-auto text-center">
          <h2 
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-black mb-6"
            style={{ fontFamily: 'Rowdies, cursive' }}
          >
            Flavors That Rotate Seasonally
          </h2>
          <p className="text-gray-700 mb-12 text-lg">
            ⭐No artificial colors, flavors, preservatives or high-fructose corn syrup
          </p>
          
          {/* Flavors 3-column Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-12 gap-y-6 text-left max-w-4xl mx-auto">
            {/* Column 1 */}
            <div className="space-y-6">
              {seasonalFlavorsCol1.map((flavor, index) => (
                <p key={index} className="text-center md:text-left">
                  <span className="font-bold text-lg" style={{ fontFamily: 'Rowdies, cursive' }}>
                    {flavor.name}
                  </span>
                  {flavor.starred && <span className="text-yellow-400 ml-1">⭐</span>}
                </p>
              ))}
            </div>
            
            {/* Column 2 */}
            <div className="space-y-6">
              {seasonalFlavorsCol2.map((flavor, index) => (
                <p key={index} className="text-center md:text-left">
                  <span className="font-bold text-lg" style={{ fontFamily: 'Rowdies, cursive' }}>
                    {flavor.name}
                  </span>
                  {flavor.starred && <span className="text-yellow-400 ml-1">⭐</span>}
                </p>
              ))}
            </div>
            
            {/* Column 3 */}
            <div className="space-y-6 col-span-2 md:col-span-1">
              {seasonalFlavorsCol3.map((flavor, index) => (
                <p key={index} className="text-center md:text-left">
                  <span className="font-bold text-lg" style={{ fontFamily: 'Rowdies, cursive' }}>
                    {flavor.name}
                  </span>
                  {flavor.starred && <span className="text-yellow-400 ml-1">⭐</span>}
                </p>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Menu;
// Build 1765603971
